import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { PaziaFormButton, PaziaInput, PaziaLoader, PaziaSwitch } from "components/atoms";
import { useMetaPixelConfig, shopApi, MetaPixelConfig } from "api/shop";
import { useToaster } from "utils";
import { useState, useEffect } from "react";

const FORM_NAMES = {
  PIXEL_ID: "pixelId",
  ENABLED: "enabled",
} as const;

const schema = yup
  .object({
    [FORM_NAMES.PIXEL_ID]: yup.string().default(""),
    [FORM_NAMES.ENABLED]: yup.boolean().default(false),
  })
  .required();

interface FormValues {
  pixelId: string;
  enabled: boolean;
}

export const ShopMetaPixelForm = () => {
  const { metaPixelConfig, isLoading, invalidate } = useMetaPixelConfig();
  const { showSuccess, showError } = useToaster();
  const [isSaving, setIsSaving] = useState(false);

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      [FORM_NAMES.PIXEL_ID]: metaPixelConfig?.pixelId ?? "",
      [FORM_NAMES.ENABLED]: metaPixelConfig?.enabled ?? false,
    },
  });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (metaPixelConfig) {
      reset({
        [FORM_NAMES.PIXEL_ID]: metaPixelConfig.pixelId ?? "",
        [FORM_NAMES.ENABLED]: metaPixelConfig.enabled ?? false,
      });
    }
  }, [metaPixelConfig, reset]);

  const onSubmit = async (data: FormValues) => {
    setIsSaving(true);
    try {
      const trimmedPixelId = data.pixelId.trim();
      const formattedData: MetaPixelConfig = {
        pixelId: trimmedPixelId === "" ? null : trimmedPixelId,
        enabled: data.enabled,
      };

      await shopApi.updateMetaPixelConfig(formattedData);
      showSuccess("Zapisano ustawienia Piksela Facebook");
      invalidate();
    } catch (error) {
      showError("Nie udało się zapisać ustawień Piksela Facebook");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <PaziaLoader position="center" />;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="grid max-w-3xl gap-6">
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <h3 className="mb-4 text-lg font-medium text-slate-800 dark:text-slate-200">Meta Pixel (Facebook Pixel)</h3>
          <div className="grid gap-4">
            <PaziaInput
              name={FORM_NAMES.PIXEL_ID}
              label="ID Piksela Facebook"
              placeholder="np. 123456789012345"
              tooltip="Identyfikator Piksela znajdziesz w Meta Business Manager w sekcji Menedżer zdarzeń (Events Manager), wybierając swój Piksel."
            />
            <PaziaSwitch name={FORM_NAMES.ENABLED} label="Włącz Piksel Facebook" />
          </div>
        </div>

        <div className="flex justify-start">
          <PaziaFormButton disabled={isSaving} />
        </div>
      </form>
    </FormProvider>
  );
};
